import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../services/supabaseClient';
import { Employee } from '../types';
import { setInternalOperatorSession } from '../services/internalSession';

export type AuthRole = 'admin' | 'super_admin' | 'user';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  employee: Employee | null;
  authRole: AuthRole | null;
  companyId: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshEmployee: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [authRole, setAuthRole] = useState<AuthRole | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const extractClaimRole = useCallback((user: User | null): AuthRole | null => {
    if (!user) return null;
    const appRole = typeof user.app_metadata?.role === 'string' ? user.app_metadata.role : '';
    const userRole = typeof user.user_metadata?.role === 'string' ? user.user_metadata.role : '';
    const internalRole = typeof user.app_metadata?.internal_role === 'string' ? user.app_metadata.internal_role : '';
    const normalized = [internalRole, appRole, userRole].map((r) => r.toLowerCase()).find(Boolean);
    if (normalized === 'super_admin') return 'super_admin';
    if (normalized === 'admin') return 'admin';
    if (normalized === 'user') return 'user';
    return null;
  }, []);

  const fetchEmployee = useCallback(async (user: User) => {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    if (error) return;
    if (data) {
      const emp: Employee = {
        id: data.id,
        name: data.name ?? 'Unknown',
        email: data.email ?? '',
        role: data.role ?? 'Employee',
        team: data.team ?? data.department ?? '',
        managerId: data.manager_id ?? data.direct_manager_id ?? undefined,
        authRole: data.auth_role ?? 'user',
        userId: data.user_id ?? undefined,
        invitedAt: data.invited_at ?? undefined,
        lastLoginAt: data.last_login_at ?? undefined,
        companyId: data.company_id ?? undefined,
      };
      setEmployee(emp);
      setAuthRole((data.auth_role as AuthRole) ?? 'user');
      setCompanyId(data.company_id ?? null);

      await supabase
        .from('employees')
        .update({ last_login_at: new Date().toISOString() })
        .eq('user_id', user.id);
    } else {
      const claimRole = extractClaimRole(user);
      setEmployee(null);
      setAuthRole(claimRole);
      setCompanyId(null);
    }
  }, [extractClaimRole]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) {
        fetchEmployee(s.user).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }).catch(() => {
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user) {
        setLoading(true);
        fetchEmployee(s.user).finally(() => setLoading(false));
      } else {
        setEmployee(null);
        setAuthRole(null);
        setCompanyId(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchEmployee]);

  const handleSignIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setEmployee(null);
    setAuthRole(null);
    setCompanyId(null);
    setInternalOperatorSession(false);
  };

  const refreshEmployee = async () => {
    if (session?.user) await fetchEmployee(session.user);
  };

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    employee,
    authRole,
    companyId,
    loading,
    isAuthenticated: !!session?.user,
    isAdmin: authRole === 'admin' || authRole === 'super_admin',
    signIn: handleSignIn,
    signOut: handleSignOut,
    refreshEmployee,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
